import { MinSecTime, TimerThresholdHandler } from "../../models/time";
import { convertMillisecToTime } from "./utils";
import { noop } from "../common";

/**
 * Default of 100ms period
 */
export const TIMER_DFLT_PERIOD = 100;

export interface TimerHandlerOptions {
  /**
   * Initial time of the timer, must be integer value, in seconds.
   */
  seconds: number;
  /**
   * Period to update the timer, in milliseconds
   */
  timerPeriod?: number;

  onTimerStarted?: () => void;
  onTimerStopped?: () => void;
  onTimerFinished?: () => void;

  /**
   * To handle a progressive list of threshold. Threshold are handled
   * sequentially so the array is assumed to be ordered
   */
  thresholdHandlers?: TimerThresholdHandler[];
}

export class TimerHandler {
  // --------------------------------------------------------------------------
  //    Attributes
  // --------------------------------------------------------------------------
  public initialSeconds!: number;
  public timerPeriod!: number;

  // Remaining time in milliseconds
  private _remainingMilliseconds!: number;
  // Save the current remaining time to avoid recalculation after each period
  private _remainingTime!: MinSecTime;
  // Remaining time in percentage
  private _remainingPercentage!: number;
  // The interval which regularly updates the remainingXXX info
  private _timerInterval?: number;

  private _onTimerStarted!: () => void;
  private _onTimerStopped!: () => void;
  private _onTimerFinished!: () => void;

  private _thresholdHandlers!: TimerThresholdHandler[];
  private _thresholdIdx!: number;

  // --------------------------------------------------------------------------
  //    Initialisation
  // --------------------------------------------------------------------------
  constructor(options: TimerHandlerOptions) {
    this.reset(options);
  }

  reset(opts: TimerHandlerOptions) {
    this.initialSeconds = opts.seconds;
    this.timerPeriod =
      opts.timerPeriod || this.timerPeriod || TIMER_DFLT_PERIOD;

    this._onTimerStarted = opts.onTimerStarted || this._onTimerStarted || noop;
    this._onTimerStopped = opts.onTimerStopped || this._onTimerStopped || noop;
    this._onTimerFinished =
      opts.onTimerFinished || this._onTimerFinished || noop;

    this._thresholdHandlers =
      opts.thresholdHandlers || this._thresholdHandlers || [];
    this._thresholdIdx = -1;

    this._remainingMilliseconds = 1000 * this.initialSeconds;
    this._remainingPercentage = 100;
    this._remainingTime = convertMillisecToTime(this._remainingMilliseconds);
  }

  // --------------------------------------------------------------------------
  //    Getter
  // --------------------------------------------------------------------------
  get remainingMilliseconds(): number {
    return this._remainingMilliseconds;
  }

  /**
   * @returns User-friendly display of remaining minutes-seconds time
   */
  get remainingDisplayTime(): string {
    // `padStart` support:
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/padStart
    const strMin = this._remainingTime.minutes.toString().padStart(2, "0");
    const strSec = this._remainingTime.seconds.toString().padStart(2, "0");
    return `${strMin}:${strSec}`;
  }

  get remainingTime(): MinSecTime {
    return this._remainingTime;
  }

  get remainingPercentage(): number {
    return this._remainingPercentage;
  }

  // --------------------------------------------------------------------------
  //    Methods
  // --------------------------------------------------------------------------
  /**
   * Start timer. Values are not reset and assumed to be valid
   */
  start() {
    this._timerInterval = window.setInterval(
      () => this.decreaseTime(this.timerPeriod),
      this.timerPeriod
    );

    this._onTimerStarted();
  }

  /**
   * Stop (pause) the timer. Starting again will resume if the values have not
   * been reset
   */
  stop() {
    this.clear();
    this._onTimerStopped();
  }

  clear() {
    window.clearInterval(this._timerInterval);
  }

  // Gracefully finish the timer
  private finish() {
    this.clear();
    this._onTimerFinished();
  }

  private decreaseTime(amount: number) {
    this._remainingMilliseconds -= amount;

    // Update percentage
    this._remainingPercentage =
      (100 * this.remainingMilliseconds) / (1000 * this.initialSeconds);

    // Update every second
    if (this.remainingMilliseconds % 1000 === 0) {
      // Minus one second!
      this._remainingTime = convertMillisecToTime(this.remainingMilliseconds);

      // Check threshold handling every second only to save computation
      if (this._thresholdIdx < this._thresholdHandlers.length - 1) {
        const { type, threshold, callback } = this._thresholdHandlers[
          this._thresholdIdx + 1
        ];

        const triggerThreshold =
          (type === "milliseconds" &&
            this.remainingMilliseconds <= threshold) ||
          (type === "percentage" && this.remainingPercentage <= threshold);

        if (triggerThreshold) {
          callback();
          this._thresholdIdx++;
        }
      }
    }

    // Finish the timer
    if (this.remainingMilliseconds <= 0) {
      this.finish();
    }
  }
}
